import { useMemo, useState } from "react";
import { Microscope, Droplets } from "lucide-react";

const ACCENT = "#b45309";

/* ── Sediment element renderers (drawn in a circular microscope tile) ───────── */
const Tile = ({ src, alt, letter }) => (
  <div className="flex flex-col items-center">
    <div className="relative rounded-full overflow-hidden border-4 border-slate-800 shadow-md" style={{ width: 96, height: 96 }}>
      <img src={src} alt={alt} className="w-full h-full object-cover" loading="lazy" />
      <span className="absolute top-1 left-1.5 text-xs font-bold text-slate-800 bg-white/75 rounded px-1 z-10">{letter}</span>
    </div>
  </div>
);

/* Photorealistic ×400 sediment photomicrographs (fixed reference set). */
const SEDIMENT_IMG = {
  struvite: "https://static.prod-images.emergentagent.com/jobs/71abba4a-702d-4e02-b451-e9e11020a1be/images/cb3a79dd207251dddef5bd22a8743ca20c639749ae62ccb3a3bf207dde8eba66.png",
  caox: "https://static.prod-images.emergentagent.com/jobs/71abba4a-702d-4e02-b451-e9e11020a1be/images/d03590aae0c696ea47ca460c3d8c359880e56f68ad2db03eebc997ccf965ca22.png",
  cast: "https://static.prod-images.emergentagent.com/jobs/71abba4a-702d-4e02-b451-e9e11020a1be/images/491da2a2a4951edc88bd8f771ef305dfdb618d61285e086bb71e876233bae465.png",
  pyuria: "https://static.prod-images.emergentagent.com/jobs/71abba4a-702d-4e02-b451-e9e11020a1be/images/b8fd65470016eddac443ee96b5a10f8339fe4115103d36aef326aa4ef57ae7ec.png",
};

const KINDS = [
  { id: "struvite", name: "Struvite (triple phosphate) crystal" },
  { id: "caox", name: "Calcium oxalate dihydrate crystal" },
  { id: "cast", name: "Granular cast" },
  { id: "pyuria", name: "WBCs + bacteria (active sediment)" },
];
const LETTERS = ["A", "B", "C", "D"];

export function useSedimentField() {
  return useMemo(() => [...KINDS].sort(() => Math.random() - 0.5), []);
}

export function SedimentField({ layout, answers, onAnswer, revealed }) {
  const correctLetterFor = (kindId) => LETTERS[layout.findIndex((k) => k.id === kindId)];
  // Randomise the order the findings are ASKED in (independent of the A–D tile
  // positions, which are already shuffled) so the question order isn't predictable.
  const prompts = useMemo(() => [...KINDS].sort(() => Math.random() - 0.5), []);
  return (
    <div data-testid="sediment-scope">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
        <Microscope className="w-3.5 h-3.5" /> Sediment · ×400 (Sedi-stain)
      </div>
      <div className="grid grid-cols-4 gap-2 mb-4">
        {layout.map((k, i) => (
          <Tile key={k.id} letter={LETTERS[i]} src={SEDIMENT_IMG[k.id]} alt={k.name} />
        ))}
      </div>
      <div className="space-y-2">
        {prompts.map((k) => {
          const correct = correctLetterFor(k.id);
          return (
            <div key={k.id} className="flex items-center justify-between gap-2 flex-wrap">
              <span className="text-[13px] text-slate-700 font-medium">Find the {k.name}</span>
              <div className="flex gap-1">
                {LETTERS.map((L) => {
                  const sel = answers[k.id] === L;
                  let cls = "border-slate-300 bg-white text-slate-600 hover:border-slate-400";
                  if (revealed) {
                    if (L === correct) cls = "border-emerald-400 bg-emerald-50 text-emerald-700";
                    else if (sel) cls = "border-rose-400 bg-rose-50 text-rose-600";
                    else cls = "border-slate-200 bg-white text-slate-300";
                  } else if (sel) cls = "border-[#b45309] bg-amber-50 text-[#b45309]";
                  return (
                    <button key={L} type="button" disabled={revealed} onClick={() => onAnswer(k.id, L)} data-testid={`sediment-${k.id}-${L}`} className={`w-8 h-8 rounded-md border-2 text-sm font-bold transition-all ${cls}`}>
                      {L}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── USG ↔ azotaemia correlation reference ─────────────────────────────────── */
export function UsgAzotaemiaPanel({ species = "dog" }) {
  const thresh = species === "cat" ? "1.035" : "1.030";
  const rows = [
    { usg: `Concentrated (≥ ${thresh})`, az: "Azotaemic", read: "Pre-renal — kidneys still concentrating; rehydrate & recheck", tone: "#10b981" },
    { usg: `Poorly conc. (< ${thresh})`, az: "Azotaemic", read: "Renal — inappropriate concentration with azotaemia", tone: "#ef4444" },
    { usg: "Isosthenuric (~1.008–1.012)", az: "Azotaemic", read: "Renal — fixed at plasma osmolality", tone: "#ef4444" },
    { usg: "Dilute (< 1.008)", az: "Not azotaemic", read: "Active dilution — consider PU/PD causes", tone: "#f59e0b" },
  ];
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="usg-azotaemia">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
        <Droplets className="w-3.5 h-3.5" /> USG ↔ azotaemia correlation ({species})
      </div>
      <div className="space-y-1">
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-[1fr_auto] gap-2 items-center text-[12px] border-l-4 pl-2 py-1" style={{ borderColor: r.tone }}>
            <div><span className="font-semibold text-slate-800">{r.usg}</span> <span className="text-slate-400">+ {r.az}</span></div>
            <div className="text-slate-600 text-[11px] text-right max-w-[55%]">{r.read}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Collection-method effects ─────────────────────────────────────────────── */
const METHODS = [
  { id: "cysto", name: "Cystocentesis", effect: "Cleanest sample — sterile for culture; expect a few RBCs from the needle. Best for bacteria/cells interpretation." },
  { id: "free", name: "Free-catch / mid-stream", effect: "Convenient but contaminated by the lower tract/genital tract — squamous cells, bacteria and debris are common artefacts; not ideal for culture." },
  { id: "cath", name: "Catheterisation", effect: "Semi-sterile; can introduce blood/transitional cells from the catheter — interpret cells with that caveat." },
];
export function CollectionMethodNote() {
  const [pick, setPick] = useState("cysto");
  const m = METHODS.find((x) => x.id === pick);
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="collection-method">
      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Collection-method effects</div>
      <div className="flex gap-1.5 mb-2">
        {METHODS.map((x) => (
          <button key={x.id} type="button" onClick={() => setPick(x.id)} data-testid={`method-${x.id}`} className={`text-[11px] font-semibold rounded-md border-2 px-2.5 py-1 transition-colors ${pick === x.id ? "border-[#b45309] bg-amber-50 text-[#b45309]" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
            {x.name}
          </button>
        ))}
      </div>
      <div className="text-[12px] text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-3 py-2" data-testid="method-effect">{m.effect}</div>
    </div>
  );
}
